import React, { useEffect } from "react";
import { css, Theme, useTheme } from "@emotion/react";
import { useLocation, useNavigate } from "react-router";
import { Helmet } from "react-helmet-async";

import Manage from "core/Components/Management/Manage";
import { useAppState } from "core/contexts/AppContext";
import { useSSRStatus } from "core/contexts/SSRStatusContext";
import useHeaderHeight from "core/hooks/useHeaderHeight";
import useMetadata, { MetaData } from "core/hooks/useMetadata";
import useRouter from "core/hooks/useRouter";
import { FLAG_SHOP_ONLINE_VIDEOS } from "core/includes/featureFlags";
import { hasFeatureFlag } from "core/includes/site";
import window from "core/includes/window";

import pages from "pages/config/pages";
import ErrorPage from "pages/Components/ErrorPage/ErrorPage";


type Props = {
    stage: string | null,
    showBreadcrumb: boolean,
    hideHeaderCountry: boolean,
    urlSegmentName?: string,
};

/**
 * Handles the main rendering of pages.  Returns error pages if needed.
 * @constructor
 */
export const normalise = (path: string): string => path.replace(/\/$/, '') || '/';

const Main: React.FC<Props> = ({ stage, showBreadcrumb, hideHeaderCountry = false, urlSegmentName }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [appState, setAppState] = useAppState();
    const user = appState?.user;
    const headerHeight = useHeaderHeight({ showBreadcrumb, hideHeaderCountry });
    const theme = useTheme();

    const [data, loading, _error, internalPage] = useRouter(stage, urlSegmentName);
    let error = _error;
    const { setStatusCode, setRedirect } = useSSRStatus();
    const page = data?.page;
    const metaData = useMetadata(page);
    const hasShopOnlineVideoFeatureFlag = hasFeatureFlag(appState.featureFlags, FLAG_SHOP_ONLINE_VIDEOS);

    useEffect(() => {
        // loading is used for displaying <Footer>
        setAppState({ loading });
    }, [loading, setAppState]);

    if (page?.__typename === 'RedirectorPage') {
        if (page.linkUrl.indexOf('http') === 0) {
            window.location = page.linkUrl;
        } else {
            navigate(page.linkUrl);
        }
        return null;
    }

    // Draft/product page URLs (e.g. /-26, /-10) were removed from sitemap (WRT-18362).
    // Some still exist in the backend DB, so we check the URL pattern directly
    // rather than relying on the backend returning null.
    // Must be checked before the canonical redirect to avoid 301-ing to an unrelated page.
    const isRemovedSitemapPage = /\/-\d+\/?$/.test(location.pathname);

    // page is undefined on first render while the GraphQL query is in flight;
    // once data resolves, redirect to the canonical path if the current URL doesn't match.
    if (!isRemovedSitemapPage && page?.absoluteUrl) {
        const rawPath = normalise(new URL(page.absoluteUrl).pathname);
        const canonicalPath = rawPath === '/home' ? '/' : rawPath;
        const requestPath = normalise(location.pathname);
        if (canonicalPath !== requestPath) {
            setRedirect(canonicalPath);
            navigate(canonicalPath, { replace: true });
            return null;
        }
    }

    let Component = page && Object.prototype.hasOwnProperty.call(pages, page.__typename) && pages[page.__typename];

    // Treat null page (not loading, no error) as 404
    const isMissingPage = !page && !loading && !error && !internalPage && data !== undefined;
    // Disallow access to OnlineEventPage without (1) correct feature flag or (2) urlSegmentName
    const isInvalidOnlineEventPage = page?.__typename === "OnlineEventPage" &&
        (!hasShopOnlineVideoFeatureFlag || !urlSegmentName);
    if (isMissingPage || isInvalidOnlineEventPage) {
        error = "404";
    }

    if (isRemovedSitemapPage) {
        error = "410";
    }

    // Signal status to SSR server
    if (error === "404" || error === "410") {
        setStatusCode(Number(error));
    }

    const style = css`
        padding-top: ${headerHeight}px;
        ${page && page.isUsingAltDesign && fontOverrides(theme)};
    `;

    return (
        <div css={style}>
            {!!user && internalPage === "profile" && hasShopOnlineVideoFeatureFlag && (
                <Manage />
            )}
            {(error || (page && !page.isClickable)) && (
                <ErrorPage error={(error === "404" || error === "410") ? "404" : "500"} />
            )}
            {(!error && page && page.isClickable && Component) && (
                <>
                    <Helmet>
                        {metaData.map((meta: MetaData, index) => {
                            switch (meta.element) {
                                case 'link': return <link key={`meta-key-${index}`} {...meta.props} />;
                                case 'title': return <title key={`meta-key-${index}`} {...meta.props} />;
                                default: return <meta key={`meta-key-${index}`} {...meta.props} />;
                            }
                        })}
                    </Helmet>

                    <Component {...page && { data: page }} />
                </>
            )}
        </div>
    );
};

// Only used if the page has "isUsingAltDesign" set to true.
const fontOverrides = (theme: Theme) => css`
    h1, h2, h3, h4, h5, h6, button {
        font-family: ${theme.fonts.figtree};
        font-weight: ${theme.fonts.weights.medium};
    }
    
    p, blockquote, li, article, section {
        font-family: ${theme.fonts.openSans};
        font-weight: ${theme.fonts.weights.light};
    }
    
`;

export default Main;
